// ============================================================
// AttackerControlsTab - the actor's COCKPIT (v8.224.0).
//
// The macro bar tops the tab (v8.231.0 - the weapon hotbar is gone;
// armoury dblclick, the 1-9 keys and macros switch weapons). Then the
// top zone, always visible: the effective profile card (with its edit
// pencil - one weapon readout, v8.229.0), the switch bank, and the
// modifier ledger - the state of the next attack at a glance. Below
// it, titled collapsible sections: Attack Setup, Weapon Overrides,
// Forecast (the stat strip). The old tab was nine widgets in one
// unstyled column with 8-9px type; the sections and the 10px floor
// (--font-size-xs) are the point.
//
// Deliberately still .jsx: the attacker context is untyped, and a .tsx
// rewrite would be a wall of `any` - the layout is what changed.
// ============================================================
import { memo } from 'react';
import styles from '../styles/AttackerPanel.module.css';
import { hasActiveOverrides } from '../engine/weaponOverrides';
import AttackToggles from './AttackToggles';
import TargetingConsole from './controls/TargetingConsole';
import FiringSolutions from './controls/FiringSolutions';
import RangeRail from './controls/RangeRail';
import ModifierLedger from './controls/ModifierLedger';
import SwitchBank from './controls/SwitchBank';
import CustomModifier from './CustomModifier';
import WeaponOverridesEditor from './WeaponOverridesEditor';
import EffectiveProfileCard from './EffectiveProfileCard';
import StatStrip from './controls/StatStrip';
import AttackMacros from './AttackMacros';
import { useAttacker } from '../contexts/AttackerContext';
import { useTargets } from '../contexts/TargetsContext';
import { useSession } from '../contexts/SessionContext';
import useTargetFigureSheet from '../hooks/useTargetFigureSheet';

function AttackerControlsTab() {
  const atk = useAttacker();
  const { currentTab } = useTargets();
  const { isLocked } = useSession();
  const target = currentTab?.target || null;
  const attackLocked = isLocked('attack');
  // The macro bar tops both branches: loading a macro ARMS its weapon
  // (macroWeaponIndex), so with nothing armed it is still a live door.
  const macroBar = (
    <AttackMacros
      macros={atk.macros || []}
      onLoadMacro={atk.onLoadMacro}
      onSaveMacro={atk.onSaveMacro}
      onDeleteMacro={atk.onDeleteMacro}
      onRequestName={atk.onRequestMacroName}
    />
  );
  if (!atk.slot || atk.slot.isEmpty) {
    return (
      <div className={styles.tabContent}>
        {macroBar}
        <div className={styles.noWeaponHint}>Select a weapon from the Armory tab to configure attack controls.</div>
      </div>
    );
  }
  return (
    <div className={styles.tabContent}>
      {macroBar}
      <CockpitTop atk={atk} target={target} locked={attackLocked} />
      <AttackSetupSection atk={atk} target={target} locked={attackLocked} />
      {/* Open by default since v8.238.1 - the user's ruling. */}
      <details className={styles.section} open data-testid="section-overrides">
        <summary className={styles.sectionSummary}>
          This attack only
          {hasActiveOverrides(atk.weaponOverrides) && <span className={styles.sectionBadge}>Modified</span>}
        </summary>
        <WeaponOverridesEditor
          slot={atk.slot}
          value={atk.weaponOverrides}
          onChange={atk.setWeaponOverrides}
          disabled={attackLocked}
        />
      </details>
      <details className={styles.section} open data-testid="section-forecast">
        <summary className={styles.sectionSummary}>Forecast</summary>
        <StatStrip result={atk.probabilityResult} loading={atk.probabilityLoading} />
      </details>
    </div>
  );
}

/** "On by itself against this target: Hatred (Orks)." - or nothing. */
const onBy = (reason) => (reason ? ` On by itself against this target: ${reason}.` : '');

/** The three switches' tooltips, naming the talents that grant or imply them. */
function switchTitles(stats, atk) {
  return {
    hatred: 'Hatred: +10 WS in melee, auto-confirm Righteous Fury on all attacks.' + onBy(atk.autoHatred),
    reroll:
      'Re-roll Damage: roll twice, take the better result.' +
      onBy(atk.autoRerollDamage) +
      (stats?.hasHunterOfAliens ? ' Hunter of Aliens grants this vs xenos.' : '') +
      (stats?.hasScourgeOfHeretics ? ' Scourge of Heretics grants this vs heretics and witches.' : ''),
  };
}

/** The switch bank's rockers: what is simply on or off for the next attack. Declarative. */
function cockpitSwitches(atk, titles) {
  return [
    {
      key: 'hatred',
      label: 'Hatred',
      checked: atk.hatred,
      onChange: atk.setHatred,
      title: titles.hatred,
      lamp: 'amber',
    },
    {
      key: 'rerollDamage',
      label: 'Re-roll Dmg',
      checked: atk.rerollDamage,
      onChange: atk.setRerollDamage,
      title: titles.reroll,
      lamp: 'amber',
    },
    {
      key: 'ignoreCalledShot',
      label: 'Called Shot Free',
      checked: atk.ignoreCalledShot,
      onChange: atk.setIgnoreCalledShot,
      title: 'Ignore the Called Shot penalty: a called shot is made at no penalty while this is on.',
      lamp: 'amber',
    },
  ];
}

/** TOP ZONE: what the next attack IS - the effective profile, the switch bank, the modifier ledger. */
function CockpitTop({ atk, target, locked }) {
  const titles = switchTitles(atk.attackerCharStats, atk);
  const { attackToggles, ledgerData } = atk;
  return (
    <div className={styles.cockpitTop} data-testid="cockpit-top">
      <EffectiveProfileCard
        slot={atk.effectiveSlot || atk.slot}
        isMelee={atk.isMelee}
        attackMod={atk.attackMod}
        targetSize={target?.size || 'average'}
        rangeBand={atk.rangeBand}
        hatred={atk.hatred}
        frenzied={atk.frenzied}
        allOutAttack={!!attackToggles.allOutAttack}
        aimed={!!(attackToggles.aimHalf || attackToggles.aimFull)}
        attackerCharStats={atk.attackerCharStats}
        modified={hasActiveOverrides(atk.weaponOverrides)}
        onEdit={() => atk.setEditingSlot(atk.selectedSlot)}
      />
      <SwitchBank switches={cockpitSwitches(atk, titles)} />
      <ModifierLedger
        chips={ledgerData?.chips ?? []}
        total={ledgerData?.total ?? 0}
        breakdown={ledgerData?.breakdown ?? []}
        locked={locked}
      />
    </div>
  );
}

/** WHERE TO HIT: the silhouette and the list, two views of one choice, driven by the same shared writers. */
function WhereToHit({ atk, target, locked, calledShot, zones }) {
  const figureSheet = useTargetFigureSheet();
  return (
    <>
      <div className={styles.setupCaption}>Where to hit</div>
      <div className={styles.setupRow}>
        <TargetingConsole
          target={target}
          figureSheet={figureSheet}
          calledShot={calledShot}
          calledShotReduction={atk.calledShotReduction}
          disabled={locked}
          {...zones}
        />
        <FiringSolutions
          target={target}
          solutions={atk.firingSolutions}
          loading={atk.firingSolutionsLoading}
          calledShot={calledShot}
          disabled={locked}
          {...zones}
        />
      </div>
    </>
  );
}

/**
 * Attack Setup. The console owns Called Shot: a zone click engages the
 * -20 AND the location in one gesture (v8.232.0). v8.246.1, the user's
 * layout: a COMPACT targeting box, then range / stances / situational
 * mods as their own visually separated groups. The rest of the row is the
 * FIRING SOLUTIONS board (v8.250.0 - target FACTS belong on the Target
 * side; this box holds attack math). Board rows and console zones drive
 * the SAME shared called-shot writers, so the two instruments agree.
 */
function AttackSetupSection({ atk, target, locked }) {
  const calledShot = !!atk.attackToggles.calledShot;
  const zones = {
    targetLocation: atk.targetLocation,
    onEngageZone: atk.engageCalledShot,
    onClear: atk.clearCalledShot,
  };
  return (
    <details className={styles.section} open data-testid="section-setup">
      <summary className={styles.sectionSummary}>Attack setup</summary>
      <WhereToHit atk={atk} target={target} locked={locked} calledShot={calledShot} zones={zones} />
      <div className={styles.setupGroup}>
        <RangeRail
          rangeBandChoice={atk.rangeBandChoice}
          rangeBandFromMap={atk.rangeBandFromMap}
          mapMetres={atk.mapMetres}
          setRangeBand={atk.setRangeBand}
          isMelee={atk.isMelee}
          byHand={atk.positionsCount === false}
          disabled={locked}
        />
      </div>
      <div className={styles.setupGroup}>
        <div className={styles.setupCaption}>How you attack</div>
        <AttackToggles
          toggles={atk.attackToggles}
          setToggles={atk.setAttackToggles}
          isMelee={atk.isMelee}
          disabled={locked}
          hasBerserkCharge={atk.hasBerserkCharge}
          hasHammerBlow={atk.hasHammerBlow}
          defensiveStance={atk.defensiveStanceActive}
          onToggleDefensiveStance={atk.onToggleDefensiveStance}
        />
      </div>
      <div className={styles.setupGroup}>
        <div className={styles.setupCaption}>Circumstances</div>
        <CustomModifier
          modifiers={atk.attackCustomMods}
          onAdd={(mod) => atk.setAttackCustomMods((prev) => [...prev, mod])}
          onRemove={(id) => atk.setAttackCustomMods((prev) => prev.filter((m) => m.id !== id))}
          disabled={locked}
        />
      </div>
    </details>
  );
}

export default memo(AttackerControlsTab);
